import { supabase } from '../lib/supabase';

const API_BASE_URL = import.meta.env.DEV
    ? 'http://localhost:3000/api'
    : '/api';

export const USE_SUPABASE = true;

export const MOCK_ACCESS_CODES = [
    'TASK-2025-ABC123',
    'TASK-2025-XYZ789',
    'DEMO-TEST-001',
    'TEST',
];

export interface CheckAccessResponse {
    success: boolean;
    sessionToken?: string;
    variantId?: number;
    batchNumber?: number;
    message?: string;
}

export interface TaskData {
    id: number;
    task_name: string;
    wallet_address: string;
    sib: string;
    hash_code?: string;
    instructions: string;
    screenshot_paths: string[];
    admin_message: string;
    batch_number: string;
    balance: string;
    psbt_file_url?: string;
}

export interface GetTaskResponse {
    success: boolean;
    task?: TaskData;
    error?: string;
}

export async function getMaintenanceMode(): Promise<boolean> {
    if (!USE_SUPABASE) {
        return localStorage.getItem('admin_maintenance_mode') === 'true';
    }

    try {
        const { data, error } = await supabase
            .from('admin_settings')
            .select('value')
            .eq('key', 'maintenance_mode')
            .single();

        if (error) return false;
        return data?.value === 'true';
    } catch (error) {
        console.error('Error fetching maintenance mode:', error);
        return false;
    }
}

export async function setMaintenanceMode(enabled: boolean): Promise<void> {
    if (!USE_SUPABASE) {
        localStorage.setItem('admin_maintenance_mode', enabled.toString());
        return;
    }

    try {
        await supabase
            .from('admin_settings')
            .upsert({ key: 'maintenance_mode', value: enabled.toString() });
    } catch (error) {
        console.error('Error setting maintenance mode:', error);
    }
}

export async function getBannedUsers(): Promise<string[]> {
    if (!USE_SUPABASE) {
        const stored = localStorage.getItem('admin_banned_users');
        return stored ? JSON.parse(stored) : [];
    }

    try {
        const { data, error } = await supabase
            .from('access_codes')
            .select('code')
            .eq('is_banned', true);

        if (error) throw error;
        return data.map((u: any) => u.code);
    } catch (error) {
        console.error('Error fetching banned users:', error);
        return [];
    }
}

export async function toggleBanUser(code: string): Promise<boolean> {
    const trimmedCode = code.trim().toUpperCase();

    if (!USE_SUPABASE) {
        const list = JSON.parse(localStorage.getItem('admin_banned_users') || '[]');
        const index = list.indexOf(trimmedCode);
        let newList;
        if (index >= 0) newList = list.filter((c: string) => c !== trimmedCode);
        else newList = [...list, trimmedCode];
        localStorage.setItem('admin_banned_users', JSON.stringify(newList));
        return index === -1;
    }

    try {
        const { data: existing } = await supabase
            .from('access_codes')
            .select('is_banned')
            .eq('code', trimmedCode)
            .single();

        if (!existing) {
            await supabase.from('access_codes').insert({ code: trimmedCode, is_banned: true });
            return true;
        }

        const newStatus = !existing.is_banned;
        await supabase
            .from('access_codes')
            .update({ is_banned: newStatus })
            .eq('code', trimmedCode);

        return newStatus;
    } catch (error) {
        console.error('Error toggling ban:', error);
        return false;
    }
}

export async function getAllVariantsData(): Promise<any> {
    if (!USE_SUPABASE) {
        const stored = localStorage.getItem('admin_variants_data');
        return stored ? JSON.parse(stored) : {};
    }

    try {
        const { data, error } = await supabase
            .from('task_variants')
            .select('*')
            .order('id');

        if (error) throw error;

        const variantsMap: any = {};
        data.forEach((v: any) => {
            variantsMap[v.id] = v;
        });
        return variantsMap;
    } catch (error) {
        console.error('Error fetching variants:', error);
        return {};
    }
}

export async function saveVariantData(id: number, data: Partial<TaskData>): Promise<boolean> {
    if (!USE_SUPABASE) {
        const stored = JSON.parse(localStorage.getItem('admin_variants_data') || '{}');
        stored[id] = { ...stored[id], ...data };
        localStorage.setItem('admin_variants_data', JSON.stringify(stored));
        return true;
    }

    try {
        const { error } = await supabase
            .from('task_variants')
            .upsert({ id, ...data });

        if (error) throw error;
        return true;
    } catch (error) {
        console.error('Error saving variant:', error);
        return false;
    }
}

export async function addAccessCode(code: string): Promise<{ success: boolean; message: string }> {
    const trimmedCode = code.trim().toUpperCase();
    if (!trimmedCode) return { success: false, message: 'Code cannot be empty' };

    if (!USE_SUPABASE) {
        const localCodes = JSON.parse(localStorage.getItem('admin_local_codes') || '[]');
        if (localCodes.includes(trimmedCode) || MOCK_ACCESS_CODES.includes(trimmedCode)) {
            return { success: false, message: 'Code already exists (Local)' };
        }

        localCodes.push(trimmedCode);
        localStorage.setItem('admin_local_codes', JSON.stringify(localCodes));
        return { success: true, message: 'Code added locally (Mock)' };
    }

    try {
        const { error } = await supabase
            .from('access_codes')
            .insert([{ code: trimmedCode, is_banned: false }]);

        if (error) {
            if (error.code === '23505') return { success: false, message: 'Code already exists' };
            throw error;
        }
        return { success: true, message: 'Code added successfully' };
    } catch (error) {
        console.error('Error adding code:', error);
        return { success: false, message: 'Failed to add code' };
    }
}

export async function getAllAccessCodes(): Promise<string[]> {
    if (!USE_SUPABASE) {
        const localCodes = JSON.parse(localStorage.getItem('admin_local_codes') || '[]');
        return [...MOCK_ACCESS_CODES, ...localCodes];
    }

    try {
        const { data, error } = await supabase
            .from('access_codes')
            .select('code')
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data.map((u: any) => u.code);
    } catch (error) {
        console.error('Error fetching codes:', error);
        return [];
    }
}

export async function checkAccess(accessCode: string): Promise<CheckAccessResponse> {
    const trimmedCode = accessCode.trim().toUpperCase();

    const maintenance = await getMaintenanceMode();
    if (maintenance) {
        return {
            success: false,
            message: 'System is currently under maintenance. Please try again later.'
        };
    }

    if (!USE_SUPABASE) {
        const bannedUsers = JSON.parse(localStorage.getItem('admin_banned_users') || '[]');
        if (bannedUsers.includes(trimmedCode)) {
             return { success: false, message: 'This account has been suspended.' };
        }

        const localCodes = JSON.parse(localStorage.getItem('admin_local_codes') || '[]');
        if (MOCK_ACCESS_CODES.includes(trimmedCode) || localCodes.includes(trimmedCode)) {
             const variantId = (simpleHash(trimmedCode) % 5) + 1;
             const batchNumber = 2025000 + variantId;
             return { success: true, sessionToken: 'local-' + Date.now(), variantId, batchNumber };
        }
        return { success: false, message: 'Invalid access code' };
    }

    try {
        const response = await fetch(`${API_BASE_URL}/check-access`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ accessCode: trimmedCode }),
        });

        const data = await response.json();

        if (!data.success) {
            return { success: false, message: data.message || 'Invalid access code.' };
        }

        return {
            success: true,
            sessionToken: data.sessionToken,
            variantId: data.variantId,
            batchNumber: 2025000 + data.variantId,
        };
    } catch (error) {
        console.error('Check access error:', error);
        return { success: false, message: 'Connection error. Please try again.' };
    }
}

export async function getTaskData(sessionToken: string, variantId: number): Promise<GetTaskResponse> {
    if (!USE_SUPABASE) {
        const MOCK_TASKS_LOCAL: Record<number, TaskData> = {
            1: { id: 1, task_name: 'Getting Started', wallet_address: '0xLOCAL...', sib: 'local sibfriz...', hash_code: 'LOCAL_HASH_CODE_1', instructions: 'Local instructions...', screenshot_paths: [], admin_message: '<h3>Local Mode</h3>', batch_number: 'L-1', balance: '0.00' },
            2: { id: 2, task_name: 'Security', wallet_address: '0xLOCAL...', sib: 'local sibfriz...', hash_code: 'LOCAL_HASH_CODE_2', instructions: 'Local instructions...', screenshot_paths: [], admin_message: '<h3>Local Mode</h3>', batch_number: 'L-2', balance: '0.00' },
            3: { id: 3, task_name: 'Asset Mgmt', wallet_address: '0xLOCAL...', sib: 'local sibfriz...', hash_code: 'LOCAL_HASH_CODE_3', instructions: 'Local instructions...', screenshot_paths: [], admin_message: '<h3>Local Mode</h3>', batch_number: 'L-3', balance: '0.00' },
            4: { id: 4, task_name: 'Transactions', wallet_address: '0xLOCAL...', sib: 'local sibfriz...', hash_code: 'LOCAL_HASH_CODE_4', instructions: 'Local instructions...', screenshot_paths: [], admin_message: '<h3>Local Mode</h3>', batch_number: 'L-4', balance: '0.00' },
            5: { id: 5, task_name: 'Recovery', wallet_address: '0xLOCAL...', sib: 'local sibfriz...', hash_code: 'LOCAL_HASH_CODE_5', instructions: 'Local instructions...', screenshot_paths: [], admin_message: '<h3>Local Mode</h3>', batch_number: 'L-5', balance: '0.00' },
        };

        const task = MOCK_TASKS_LOCAL[variantId];

        const storedMessages = localStorage.getItem('admin_variants_data');
        if (storedMessages && task) {
             const adminVariants = JSON.parse(storedMessages);
             if (adminVariants[variantId]) {
                 return { success: true, task: { ...task, ...adminVariants[variantId] } };
             }
        }
        return task ? { success: true, task } : { success: false, error: 'Task not found' };
    }

    try {
        const response = await fetch(`${API_BASE_URL}/get-task`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ sessionToken, variantId }),
        });

        const data = await response.json();

        if (!response.ok) {
            return { success: false, error: data.error || 'Failed to load task' };
        }

        return { success: true, task: data };
    } catch (error) {
        console.error('Get task error:', error);
        return { success: false, error: 'Connection error' };
    }
}

export async function joinSignup(socialHandle: string, email: string, referralCode?: string, interests?: string[]): Promise<{ success: boolean; message: string }> {
    if (!USE_SUPABASE) {
        const joinList = JSON.parse(localStorage.getItem('admin_join_list') || '[]');
        joinList.push({ socialHandle, email, referralCode, interests, timestamp: new Date().toISOString() });
        localStorage.setItem('admin_join_list', JSON.stringify(joinList));

        return new Promise((resolve) => {
            setTimeout(() => {
                resolve({ success: true, message: 'Successfully joined! (Local Mode)' });
            }, 1000);
        });
    }

    try {
        const response = await fetch(`${API_BASE_URL}/join`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ socialHandle, email, referralCode, interests }),
        });

        const data = await response.json();

        if (!response.ok) {
            return { success: false, message: data.message || 'Failed to join. Please try again.' };
        }

        return { success: true, message: 'Successfully joined!' };
    } catch (error) {
        console.error('Join error:', error);
        return { success: false, message: 'Connection error. Please try again.' };
    }
}

function simpleHash(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
    }
    return Math.abs(hash);
}

export function saveSession(sessionToken: string, variantId: number, batchNumber: number): void {
    localStorage.setItem('sessionToken', sessionToken);
    localStorage.setItem('variantId', variantId.toString());
    localStorage.setItem('batchNumber', batchNumber.toString());
}

export function getSession(): { sessionToken: string | null; variantId: number | null; batchNumber: number | null } {
    const sessionToken = localStorage.getItem('sessionToken');
    const variantIdStr = localStorage.getItem('variantId');
    const batchNumberStr = localStorage.getItem('batchNumber');
    const variantId = variantIdStr ? parseInt(variantIdStr, 10) : null;
    const batchNumber = batchNumberStr ? parseInt(batchNumberStr, 10) : null;

    return { sessionToken, variantId, batchNumber };
}

export function clearSession(): void {
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('variantId');
    localStorage.removeItem('batchNumber');
}

export async function copyToClipboard(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (error) {
        console.error('Failed to copy:', error);
        try {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            return true;
        } catch {
            return false;
        }
    }
}
